import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Share2, Flag, CheckCircle2, Volume2, VolumeX, Play, Pause, Captions, Youtube } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { ReportDialog } from "../components/ReportDialog";
import { SponsoredCard } from "../components/SponsoredCard";
import { AffiliateCard } from "../components/AffiliateCard";

export default function Shorts() {
  const [shorts, setShorts] = useState([]);
  const [ads, setAds] = useState([]);
  const [affShorts, setAffShorts] = useState(null);
  const [curated, setCurated] = useState([]);
  const [reportId, setReportId] = useState(null);

  useEffect(() => {
    api.get("/shorts").then((r) => setShorts(r.data)).catch(() => {});
    api.get("/ads/active?placement=ptiplay").then((r) => setAds(r.data)).catch(() => {});
    api.get("/affiliate?placement=shorts&rotate=true&ab=true").then((r) => setAffShorts(r.data[0] || null)).catch(() => {});
    api.get("/curated-shorts").then((r) => setCurated(r.data.videos || [])).catch(() => {});
  }, []);

  // Build feed: shorts + a curated tech video after every 3rd short + sponsor/affiliate cards.
  const feed = [];
  let cIdx = 0;
  shorts.forEach((s, i) => {
    feed.push({ type: "short", data: s });
    if (i === 2 && affShorts) feed.push({ type: "affiliate", data: affShorts });
    if ((i + 1) % 3 === 0 && curated.length) { feed.push({ type: "curated", data: curated[cIdx % curated.length] }); cIdx += 1; }
    if ((i + 1) % 6 === 0 && ads.length) feed.push({ type: "ad", data: ads[(i / 6) % ads.length] });
  });
  // If there are no uploaded shorts yet, still show the curated tech videos.
  if (shorts.length === 0 && curated.length) curated.forEach((c) => feed.push({ type: "curated", data: c }));

  return (
    <div className="bg-black" data-testid="shorts-page">
      <div className="shorts-feed max-w-[500px] mx-auto">
        {feed.map((item, i) =>
          item.type === "short"
            ? <ShortItem key={item.data.id} s={item.data} onReport={() => setReportId(item.data.id)} />
            : item.type === "curated"
              ? <CuratedShortItem key={`cur-${item.data.id}-${i}`} v={item.data} />
              : item.type === "affiliate"
                ? <div key={`aff-${i}`} className="shorts-item h-[calc(100vh-108px)] flex items-center justify-center p-6"><div className="w-full max-w-sm"><AffiliateCard ad={item.data} placement="shorts" className="shadow-lg border-t-4 border-t-pti-red" /></div></div>
                : <div key={`ad-${i}`} className="shorts-item h-[calc(100vh-108px)] flex items-center justify-center p-6"><div className="w-full max-w-sm"><SponsoredCard ad={item.data} label="Sponsored" /></div></div>
        )}
        {feed.length === 0 && <div className="h-[60vh] flex items-center justify-center text-neutral-500">Loading Shorts…</div>}
      </div>
      <ReportDialog open={!!reportId} onOpenChange={(o) => !o && setReportId(null)} contentType="short" contentId={reportId} />
    </div>
  );
}

function CuratedShortItem({ v }) {
  return (
    <div className="shorts-item h-[calc(100vh-108px)] flex items-center justify-center p-5" data-testid={`shorts-curated-${v.id}`}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-6 h-6 bg-pti-red text-white"><Youtube size={14} /></span>
          <div className="leading-tight">
            <div className="text-white font-display font-bold text-sm">Curated Tech Shorts</div>
            <div className="text-[11px] text-neutral-400">Selected by PictureThisInk</div>
          </div>
        </div>
        <div className="relative w-full overflow-hidden bg-black border border-neutral-800" style={{ aspectRatio: "9 / 16", maxHeight: "70vh" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${v.id}`}
            title={v.title || "Curated tech short"}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        {(v.title || v.channel) && (
          <div className="mt-3">
            {v.title && <p className="text-white text-sm font-semibold leading-tight line-clamp-2">{v.title}</p>}
            <div className="mt-1 flex items-center justify-between gap-2">
              <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-pti-red hover:underline">
                <Youtube size={12} /> {v.channel ? `via YouTube · ${v.channel}` : "Watch on YouTube"}
              </a>
              {v.published && <span className="text-[11px] text-neutral-500 shrink-0">{new Date(v.published).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ShortItem({ s, onReport }) {
  const { user } = useAuth();
  const { t } = useT();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [following, setFollowing] = useState(false);
  const [cc, setCc] = useState(() => localStorage.getItem("pti_cc") !== "off");
  const toggleCc = () => setCc((v) => { const nv = !v; localStorage.setItem("pti_cc", nv ? "on" : "off"); return nv; });

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => { const v = videoRef.current; if (v) { v.muted = !v.muted; setMuted(v.muted); } };
  const like = () => { setLiked(!liked); if (!liked) api.post(`/shorts/${s.id}/like`).catch(() => {}); };
  const follow = async () => {
    if (!user) return toast.error("Sign in to follow.");
    const { data } = await api.post(`/creators/${s.author.username}/follow`); setFollowing(data.following);
  };
  const share = async () => { try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); } catch {} };

  return (
    <div className="shorts-item relative h-[calc(100vh-108px)] flex items-center justify-center" data-testid={`short-${s.id}`}>
      <video
        ref={videoRef}
        src={s.thumbnail ? s.video_url : `${s.video_url}#t=0.1`}
        poster={s.thumbnail || undefined}
        loop
        muted={muted}
        playsInline
        preload="metadata"
        controls
        onClick={toggle}
        className="h-full w-full object-cover"
        aria-label={`Short: ${s.title}. Caption: ${s.caption}`}
      />
      {/* caption track note — captions required, shown as overlay text */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />

      {/* Center play/pause */}
      <button onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`short-play-${s.id}`}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-4 pointer-events-auto">
        {playing ? <Pause size={26} /> : <Play size={26} />}
      </button>

      {/* On-screen captions overlay (accessibility for deaf viewers) */}
      {cc && s.caption && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-24 w-[92%] max-w-md text-center pointer-events-none z-10" data-testid={`short-cc-text-${s.id}`} aria-live="polite">
          <span className="inline-block bg-black/80 text-white text-base font-semibold leading-snug px-3 py-1.5 rounded">{s.caption}</span>
        </div>
      )}

      {/* Info */}
      <div className="absolute bottom-4 left-4 right-16 text-white pointer-events-auto">
        <Link to={`/creators/${s.author?.username}`} className="flex items-center gap-2">
          <img src={s.author?.avatar || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100"} alt="" className="w-8 h-8 rounded-full object-cover" />
          <span className="font-bold text-sm flex items-center gap-1">{s.author?.display_name}{s.author?.verified && <CheckCircle2 size={12} className="text-pti-red" />}</span>
          <span className="text-xs text-white/70">@{s.author?.username}</span>
        </Link>
        <span className="inline-block mt-2 text-[10px] uppercase tracking-wider font-bold text-pti-red">{s.category}</span>
      </div>

      {/* Right actions */}
      <div className="absolute right-3 bottom-6 flex flex-col items-center gap-5 text-white pointer-events-auto">
        <button onClick={follow} aria-label="Follow creator" className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${following ? "bg-white text-pti-red" : "bg-pti-red"}`}>{following ? "✓" : "+"}</button>
        <button onClick={like} data-testid={`short-like-${s.id}`} aria-label="Like" className="flex flex-col items-center text-xs"><Heart size={26} fill={liked ? "#ef1831" : "none"} color={liked ? "#ef1831" : "white"} />{s.likes + (liked ? 1 : 0)}</button>
        <button aria-label="Comments" data-testid={`short-comment-${s.id}`} className="flex flex-col items-center text-xs"><MessageCircle size={26} />{s.comments_count}</button>
        <button onClick={share} data-testid={`short-share-${s.id}`} aria-label="Share" className="flex flex-col items-center text-xs"><Share2 size={26} /></button>
        <button onClick={toggleCc} data-testid={`short-cc-${s.id}`} aria-label={t("captions")} aria-pressed={cc} className="flex flex-col items-center text-xs font-bold"><Captions size={24} className={cc ? "text-pti-red" : "text-white"} /> CC</button>
        <button onClick={toggleMute} data-testid={`short-mute-${s.id}`} aria-label={muted ? "Unmute" : "Mute"}>{muted ? <VolumeX size={24} /> : <Volume2 size={24} />}</button>
        <button onClick={onReport} aria-label="Report" className="text-white/80"><Flag size={22} /></button>
      </div>
    </div>
  );
}
